import { Badge } from '@/components/ui/badge';
import { Flame, TrendingUp, Star } from 'lucide-react';

export type TierValue = 'super_hot' | 'hot' | 'standard' | null | undefined;
export type TierContext = 'property' | 'project' | 'plot' | 'commercial';

interface Props {
  tier: TierValue;
  context?: TierContext;
  isFeaturedFallback?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * Renders a package-tier badge for a listing.
 * - Properties / Plots / Commercial: Super Hot · Hot · Featured
 * - Projects: Super Hot · Hot · Trending
 */
export function TierBadge({ tier, context = 'property', isFeaturedFallback, className = '', size = 'md' }: Props) {
  const isProject = context === 'project';

  let label = '';
  let icon: JSX.Element | null = null;
  let cls = '';

  if (tier === 'super_hot') {
    label = 'Super Hot';
    icon = <Flame className="h-3 w-3" />;
    cls = 'bg-destructive text-destructive-foreground';
  } else if (tier === 'hot') {
    label = 'Hot';
    icon = <Flame className="h-3 w-3" />;
    cls = 'bg-orange-500 text-white';
  } else if (tier === 'standard') {
    if (isProject) {
      label = 'Trending';
      icon = <TrendingUp className="h-3 w-3" />;
      cls = 'bg-primary text-primary-foreground';
    } else {
      label = 'Featured';
      icon = <Star className="h-3 w-3" />;
      cls = 'bg-amber-500 text-white';
    }
  } else if (isFeaturedFallback) {
    label = 'Featured';
    icon = <Star className="h-3 w-3" />;
    cls = 'bg-secondary text-secondary-foreground';
  } else {
    return null;
  }

  const sizeCls = size === 'sm' ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5';

  return (
    <Badge className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide ${sizeCls} ${cls} ${className}`}>
      {icon}
      {label}
    </Badge>
  );
}
